/**
 * Map Selection Utilities
 *
 * TypeScript utilities for map object selection, replacing legacy select-object.js functionality.
 * These are pure functions that can be used by both React components and hooks.
 */
/**
 * Default click buffer in pixels - shared between crosshair and WMS tools
 * Features must be within this many pixels of the click to be selected
 */
export const DEFAULT_CLICK_BUFFER_PIXELS = 20;
// Distance calculation utilities
function sqr(x) {
    return x * x;
}
function dist2(v, w) {
    return sqr(v[0] - w[0]) + sqr(v[1] - w[1]);
}
function distToSegmentSquared(p, v, w) {
    const l2 = dist2(v, w);
    if (l2 === 0)
        return dist2(p, v);
    const t = ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2;
    if (t < 0)
        return dist2(p, v);
    if (t > 1)
        return dist2(p, w);
    return dist2(p, [v[0] + t * (w[0] - v[0]), v[1] + t * (w[1] - v[1])]);
}
/**
 * Calculate squared distance from a point to a LineString geometry
 */
export function distToLineStringSquared(point, coordinates) {
    if (!coordinates || coordinates.length < 2)
        return Infinity;
    let minDist = Infinity;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const dist = distToSegmentSquared(point, coordinates[i], coordinates[i + 1]);
        if (dist < minDist)
            minDist = dist;
    }
    return minDist;
}
/**
 * Calculate distance from a point to a LineString geometry
 */
export function distToLineString(point, coordinates) {
    return Math.sqrt(distToLineStringSquared(point, coordinates));
}
/**
 * Calculate squared distance from latlng to a Point feature
 * Includes zoom-dependent point padding for easier selection at high zoom
 */
export function distToPointSquared(latlng, coordinates, pointPadding) {
    const pointPaddingSquared = pointPadding ** 2;
    return (sqr(latlng.lat - coordinates[1]) +
        sqr(latlng.lng - coordinates[0]) -
        pointPaddingSquared);
}
/**
 * Calculate point padding based on zoom level
 * Makes points easier to click at higher zoom levels
 */
export function calculatePointPadding(zoom) {
    const metre = 0.000014615;
    return (metre / 55) * (2 ** (26 - zoom));
}
/**
 * Convert a pixel distance to squared degrees at a given map location
 * Uses the map's projection to accurately convert pixels to geographic distance
 * @param map - Leaflet map instance
 * @param latlng - The click location
 * @param pixels - Distance in pixels
 * @returns Squared distance in degrees (for efficient comparison)
 */
export function getMaxDistanceSquared(map, latlng, pixels) {
    // Get the click point in container coordinates
    const clickPoint = map.latLngToContainerPoint(latlng);
    // Create a point `pixels` away horizontally
    const offsetPoint = { x: clickPoint.x + pixels, y: clickPoint.y };
    const offsetLatLng = map.containerPointToLatLng(offsetPoint);
    // Calculate squared distance in degrees
    const dLat = offsetLatLng.lat - latlng.lat;
    const dLng = offsetLatLng.lng - latlng.lng;
    return dLat * dLat + dLng * dLng;
}
/**
 * Find the nearest feature to a click location.
 * Point features (poles, chambers, UPRNs) are prioritised over line features
 * (lead-ins, conduits) when both are within the click buffer — this accounts
 * for "fat finger" taps where lines meet at a point.
 *
 * @param latlng - Click location
 * @param features - Array of features to search
 * @param zoom - Current map zoom level
 * @param maxDistanceSquared - Optional maximum squared distance in degrees. Features beyond this are ignored.
 */
export function nearestFeature(latlng, features, zoom, maxDistanceSquared) {
    if (!features || features.length === 0)
        return null;
    const point = [latlng.lng, latlng.lat];
    const pointPadding = calculatePointPadding(zoom);
    let nearestPoint = null;
    let nearestLine = null;
    for (const feature of features) {
        const geometry = feature.geometry;
        if (!geometry)
            continue;
        // Check if point geometry (including Leaflet markers)
        const isPoint = geometry.type === 'Point' || feature._latlng;
        let distSqr;
        if (isPoint) {
            const coords = geometry.type === 'Point'
                ? geometry.coordinates
                : [feature._latlng.lng, feature._latlng.lat];
            distSqr = distToPointSquared(latlng, coords, pointPadding);
            if (!nearestPoint || distSqr < nearestPoint.distSqr) {
                nearestPoint = { feature, distSqr };
            }
        }
        else if (geometry.type === 'LineString') {
            distSqr = distToLineStringSquared(point, geometry.coordinates);
            if (!nearestLine || distSqr < nearestLine.distSqr) {
                nearestLine = { feature, distSqr };
            }
        }
        // Skip polygon/multipolygon for now
    }
    // If a point feature is within the click buffer, prefer it over any line
    // This matches the server-side /geo/nearest logic which gives points a
    // fatFingerRadius advantage.
    let best = null;
    if (nearestPoint && nearestLine) {
        const pointInBuffer = maxDistanceSquared === undefined || nearestPoint.distSqr <= maxDistanceSquared;
        if (pointInBuffer) {
            best = nearestPoint;
        }
        else {
            best = nearestLine.distSqr < nearestPoint.distSqr ? nearestLine : nearestPoint;
        }
    }
    else {
        best = nearestPoint || nearestLine;
    }
    if (!best)
        return null;
    // If max distance specified, reject if nearest feature is too far
    if (maxDistanceSquared !== undefined && best.distSqr > maxDistanceSquared) {
        return null;
    }
    return best.feature;
}
// Object identification utilities
/**
 * Determine if a feature represents a UPRN or structure
 */
export function getObjectType(feature) {
    const props = feature.properties;
    return props.LAYER === 'cfn:uprn' ? 'UPRN' : 'structure';
}
/**
 * Get the unique identifier for a feature
 * UPRNs use UPRN field, structures use DB_ID or OBJECTID
 */
export function getObjectId(feature) {
    const props = feature.properties;
    if (getObjectType(feature) === 'UPRN') {
        return props.UPRN;
    }
    return (props.DB_ID || props.ID || props.OBJECTID);
}
/**
 * Generate a unique key for a feature (type_id format)
 */
export function getObjectKey(feature) {
    return `${getObjectType(feature)}_${getObjectId(feature)}`;
}
/**
 * Generate a display title for a feature
 */
export function getObjectTitle(feature) {
    const props = feature.properties;
    // Use explicit title if present
    if (props.TITLE)
        return props.TITLE;
    const objectType = getObjectType(feature);
    if (objectType === 'UPRN') {
        return `UPRN ${props.UPRN || 'Unknown'}`;
    }
    // Structure title formatting
    const exchangeCode = props.EXCHANGE_1141_CODE
        ? `${props.EXCHANGE_1141_CODE}: `
        : '';
    const objectClass = props.OBJECT_CLASS;
    if (objectClass === 'POLE') {
        const dpIdent = props.DP_IDENTIFIER;
        if (dpIdent) {
            const subPole = props.SUB_POLE && props.SUB_POLE !== '00'
                ? ` - ${props.SUB_POLE}`
                : '';
            return `${exchangeCode}DP${dpIdent}${subPole} [${props.OBJECTID || ''}]`;
        }
        return `${exchangeCode}Pole [${props.OBJECTID || ''}]`;
    }
    if (['JB', 'MH'].includes(objectClass)) {
        const plantItem = props.PLANT_ITEM ? `${props.PLANT_ITEM} ` : '';
        const structNum = props.STRUCTURE_NUMBER ? ` (${props.STRUCTURE_NUMBER})` : '';
        return `${exchangeCode}${plantItem}${structNum} [${props.OBJECTID || ''}]`;
    }
    // Default fallback
    return getObjectKey(feature);
}
/**
 * Check if running on a mobile device
 */
export function isMobileDevice() {
    if (typeof window === 'undefined')
        return false;
    const cfn = window.CFN;
    if (cfn?.isMobileDevice)
        return cfn.isMobileDevice();
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
/**
 * Extract NOI reference from current URL (for PIA NOI map pages)
 */
export function getNOIRef() {
    if (typeof window === 'undefined')
        return null;
    const match = window.location.href.match(/\/pia_noi\/(.*)\/map$/);
    return match ? match[1] : null;
}
/**
 * Generate structured filename for uploads
 */
export function generateUploadFilename(file, objectType, objectId) {
    const prefix = objectType === 'UPRN' ? 'UPRN' : 'structure';
    const extMap = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
    };
    const extn = extMap[file.type] || file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'bin';
    return `${prefix}_${objectId}_${file.lastModified}.${extn}`;
}
/**
 * Parse WhatsApp filename to extract original timestamp
 * Format: "WhatsApp Image YYYY-MM-DD at HH.MM.SS"
 */
export function parseWhatsAppFilename(filename) {
    const match = filename.match(/^WhatsApp Image (\d{4}-\d\d-\d\d) at (\d+\.\d+\.\d+)(\s|\.)/);
    if (!match)
        return null;
    const changeDate = new Date(`${match[1]} ${match[2].replace(/\./g, ':')}`);
    return isNaN(changeDate.getTime()) ? null : changeDate.getTime();
}
/**
 * Calculate nearest point on a line to a given point
 * Uses turf.js if available, otherwise returns null
 */
export function nearestPointOnLine(point, lineCoordinates) {
    const turf = window.turf;
    if (!turf)
        return null;
    try {
        const line = turf.lineString(lineCoordinates);
        const pt = turf.point(point);
        const nearest = turf.nearestPointOnLine(line, pt, { units: 'metres' });
        return nearest?.geometry?.coordinates || null;
    }
    catch {
        return null;
    }
}
/**
 * Calculate popup location for a feature
 * For points, use the point. For lines, find nearest point on line.
 */
export function getPopupLocation(feature, clickLatLng) {
    const props = feature.properties;
    const geometry = feature.geometry;
    // Use pre-calculated closest point if available
    if (props.CLOSEST_POINT) {
        try {
            return JSON.parse(props.CLOSEST_POINT);
        }
        catch {
            // Fall through to calculate
        }
    }
    if (!geometry) {
        return [clickLatLng.lng, clickLatLng.lat];
    }
    // Point geometry
    if (geometry.type === 'Point') {
        return geometry.coordinates;
    }
    // Polygon - use click location
    if (['Polygon', 'MultiPolygon'].includes(geometry.type)) {
        return [clickLatLng.lng, clickLatLng.lat];
    }
    // LineString - find nearest point
    if (geometry.type === 'LineString') {
        const nearest = nearestPointOnLine([clickLatLng.lng, clickLatLng.lat], geometry.coordinates);
        return nearest || [clickLatLng.lng, clickLatLng.lat];
    }
    return [clickLatLng.lng, clickLatLng.lat];
}
